import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { authAPI } from '@/api/new-api'
import { UserRole } from '@/models/features/user.models'

import { ReservationStatus } from './get-reservation-dashboard'

export type Question = { answer: string; id: string; question: string; isRequired: boolean }

export type ManagerialBooking = { startDate: number; endDate: number; locationId: number }

export type UnavailableSlot = {
  createdBy: string
  date: number
  description: string | null
  endDate: number | null
  endTime: number | null
  id: number
  isLinkedBlock: boolean
  isPhantomBooking: boolean
  linkReservationId: string | null
  locationId: number
  reason: string
  startTime: number | null
}

type Amenity = {
  createdAt: number
  updatedAt: number
  id: number
  name: string
  description: string
  startDate: number
  endDate: number
  agreement?: string | null
  isSignatureRequired?: boolean
  regulation: string | null
  image: string
  linkedAmenityIds: number[] | null
  daysAvailable: number[]
  availableStartTime: number | null
  availableEndTime: number | null
  timeInterval: number | null
  minTime: number | null
  maxTime: number | null
  capacity: number | null
  paymentType: 'flatRate' | 'perHour' | null
  autoApproval?: boolean
  allowDoubleReservation?: boolean
  limitDailyReservations?: boolean
  weeklyLimiter: number | null
  bookingWindow: number | null
  isOnePerDay?: boolean
  hidden?: boolean
  ongoing?: boolean
  multidaySlots: {
    startDate: number
    price: number
    repeats: boolean
    weekdays: number[]
    id: string
  }[]
  multiDayBooking: true
  minimumDays: number
  maximumDays: number
  checkInTime: number
  checkOutTime: number
  extraFee: number | null
  extraFeeName: string | null
  questions: Question[]
  hourlySlots: {
    startDate: number
    startTime: number
    endTime: number
    repeats: boolean
    price: number
  }[]
  noticePeriod: number
  allowMultipleLocations?: boolean
  manualLocationSelection?: boolean
  availabilityTime: null
  saltoSpaceAccessAvailable?: boolean
  spaceType: string
  benefits: string[]
  squareFeet: number | null
  maxGuests: number | null
  deleted?: boolean
  applySpecialPricing?: boolean
  buildingId: number
  price: number
  isPaid?: boolean
  unavailableSlots: {
    weekly: UnavailableSlot[]
    regular: UnavailableSlot[]
  } | null
  managerialBooking: ManagerialBooking[] | null
}

type Location = {
  amenityId: number
  available: boolean
  createdAt: number
  description: string
  id: number
  image: string
  name: string
  updatedAt: number
}

export type User = {
  avatar: string
  createdAt: number
  deleted: boolean
  email: string
  firstName: string
  id: number
  isActive: boolean
  lastName: string
  role: UserRole
  updatedAt: number
  apartment: string | null
}

type Customer = {
  phone: number
  apartment: string
  user: {
    id: number
    email: string
    firstName: string
    lastName: string
    role: string
  }
}

type Guest = {
  buildingId: number
  createdAt: number
  creditCardId: boolean
  customerId: string
  defaultPaymentMethodId: string
  email: string
  firstName: string
  id: number
  lastName: string
  phone: number
  updatedAt: number
}

export type Charge = {
  amount: number
  chargeId: string
  convenienceFee: number
  createdAt: number
  extraFee: number | null
  id: number
  initiatedBy: string
  reservationId: number
  updatedAt: number
}

export type ReservationDetails = {
  id: number
  status: ReservationStatus
  amenityId: Amenity
  date: number
  endDate: number | null
  startTime: number
  endTime: number
  approved: boolean
  cancelled: boolean
  chargeId: string
  chargeAmount: number
  refunded: boolean
  cancelledBy: UserRole
  hidden: boolean
  createdByUser: User
  cancelledByUser: User | null
  cancelledAt: number
  approvedByUser: string | null
  approvedAt: number | null
  autoApproved: boolean | null
  refundedByUser: string
  refundedAt: number
  refundAmount: number
  discount: number | null
  rewardId: number
  signedAgreementFile: string
  locationId: Location | null
  extraFee: number
  createdAt: number
  clientId: Customer | null
  guestId: Guest | null
  questions: Question[]
  charges: Charge[]
}

export const RESERVATION_DETAILS_QUERY_KEY = 'reservationDetails'

export const getReservationDetailsQueryKey = (reservationId: string) => [
  RESERVATION_DETAILS_QUERY_KEY,
  reservationId,
]

const getReservationDetails = async (reservationId: string) => {
  const response = await authAPI.get<ReservationDetails>(`/reservation/${reservationId}`)
  return response.data
}

export const useGetReservationDetails = (reservationId: string) =>
  useQuery<ReservationDetails, AxiosError>({
    enabled: !!reservationId,
    queryFn: () => getReservationDetails(reservationId),
    queryKey: getReservationDetailsQueryKey(reservationId),
  })
